import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency } from "@/lib/api";
import { FileText, Download, Printer } from "lucide-react";

export default function PolicyDocPage() {
  const { activeEntity, entities } = useApp();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState(activeEntity?.id || "");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (entityFilter) params.entity_id = entityFilter;
        const { data } = await api.get("/reports/policy-document", { params });
        setDoc(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [entityFilter]);

  const handlePrint = () => window.print();

  if (loading || !doc) return <div className="stratum-card p-12 text-center text-[#6B7280]">Generating policy document...</div>;

  return (
    <div className="space-y-6" data-testid="policy-doc-page">
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
            <FileText size={24} className="inline mr-2 text-[#C8A961]" />
            PPE Accounting Policy Document
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">IFRS-compliant policy with 13 disclosure sections</p>
        </div>
        <div className="flex gap-3">
          <select className="stratum-input" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}>
            <option value="">Group (All Entities)</option>
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <button onClick={handlePrint} className="stratum-btn-primary flex items-center gap-2" data-testid="print-policy-btn">
            <Printer size={14} /> Print / Save PDF
          </button>
        </div>
      </div>

      {/* Document */}
      <div className="stratum-card max-w-4xl mx-auto" data-testid="policy-document">
        {/* Cover */}
        <div className="p-10 border-b border-[#E8E2D5] text-center bg-[#0B1020] text-white rounded-t-lg">
          <img src="/assets/stratum_logomark.png" alt="" className="w-14 h-14 mx-auto mb-4 rounded-xl" />
          <h1 className="font-display text-2xl font-bold mb-2">{doc.document.title}</h1>
          <p className="text-lg text-white/70">{doc.document.company}</p>
          {doc.document.registration && <p className="text-sm text-white/50 mt-1">Registration: {doc.document.registration}</p>}
          <div className="mt-4 flex justify-center gap-6 text-sm text-white/50">
            <span>Year-end: {doc.document.year_end || "Not set"}</span>
            <span>Currency: {doc.document.currency}</span>
            {doc.document.auditor && <span>Auditor: {doc.document.auditor}</span>}
          </div>
          <p className="text-xs text-white/30 mt-4">Version: {doc.document.version} | Generated: {doc.document.generated_at?.substring(0, 10)}</p>
        </div>

        {/* Summary Box */}
        <div className="p-6 bg-[#FAF8F4] border-b border-[#E8E2D5]">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="font-mono text-lg font-semibold text-[#0B1020] tabular-nums">{formatCurrency(doc.summary.total_cost)}</div>
              <div className="text-xs text-[#6B7280]">Total Cost</div>
            </div>
            <div>
              <div className="font-mono text-lg font-semibold text-[#0B1020] tabular-nums">{formatCurrency(doc.summary.total_carrying)}</div>
              <div className="text-xs text-[#6B7280]">Carrying Amount</div>
            </div>
            <div>
              <div className="font-mono text-lg font-semibold text-[#0B1020] tabular-nums">{doc.summary.asset_count}</div>
              <div className="text-xs text-[#6B7280]">Assets</div>
            </div>
            <div>
              <div className="font-mono text-lg font-semibold text-[#0B1020] tabular-nums">{doc.summary.categories_with_policy}</div>
              <div className="text-xs text-[#6B7280]">Categories</div>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="p-8 space-y-8">
          {doc.sections.map((s) => (
            <div key={s.number} id={`section-${s.number}`}>
              <h2 className="font-display text-lg font-semibold text-[#0B1020] mb-2">
                <span className="text-[#C8A961] mr-2">{s.number}.</span>{s.title}
              </h2>
              <p className="text-sm text-[#1A1A1A] leading-relaxed">{s.content}</p>

              {/* Category table after section 4 (Depreciation) */}
              {s.number === 4 && doc.category_policies.length > 0 && (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-xs border border-[#E8E2D5] rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-[#FAF8F4]">
                        <th className="px-3 py-2 text-left font-medium text-[#6B7280]">Category</th>
                        <th className="px-3 py-2 text-left font-medium text-[#6B7280]">Method</th>
                        <th className="px-3 py-2 text-right font-medium text-[#6B7280]">Useful Life</th>
                        <th className="px-3 py-2 text-right font-medium text-[#6B7280]">Residual %</th>
                        <th className="px-3 py-2 text-right font-medium text-[#6B7280]">Tax Write-off</th>
                        <th className="px-3 py-2 text-right font-medium text-[#6B7280]">Assets</th>
                        <th className="px-3 py-2 text-right font-medium text-[#6B7280]">Carrying</th>
                      </tr>
                    </thead>
                    <tbody>
                      {doc.category_policies.map((cp, i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#FAF8F4]"}>
                          <td className="px-3 py-1.5 font-medium">{cp.category}</td>
                          <td className="px-3 py-1.5">{cp.method}</td>
                          <td className="px-3 py-1.5 text-right font-mono tabular-nums">{cp.useful_life_years} years</td>
                          <td className="px-3 py-1.5 text-right font-mono tabular-nums">{cp.residual_pct}%</td>
                          <td className="px-3 py-1.5 text-right font-mono tabular-nums">{cp.tax_years} years</td>
                          <td className="px-3 py-1.5 text-right font-mono tabular-nums">{cp.asset_count}</td>
                          <td className="px-3 py-1.5 text-right font-mono tabular-nums">{formatCurrency(cp.total_carrying)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-[#E8E2D5] bg-[#FAF8F4] rounded-b-lg text-center">
          <p className="text-xs text-[#6B7280]">
            This document was generated by Stratum — The Intelligent Asset Register.
            It should be reviewed by the preparer and approved by the CFO before inclusion in the annual financial statements.
          </p>
          <p className="text-[10px] text-[#6B7280]/50 mt-2">Powered by Stratum | {doc.document.generated_at?.substring(0, 10)}</p>
        </div>
      </div>
    </div>
  );
}
